import React, { memo, useCallback, useEffect, useState } from "react";
import { RootState, useAppDispatch, useAppSelector } from "../../store/store";
import { enqueueSnackbar } from "../../store/app-slice/app";
import { GetPredictionColumns, handleClearAllInputColumns, PredictSingle, savePredictionValue } from "../../store/prediction-slice/prediction";
import { handleNotificationVariant } from "../notifications/_common";
import { useTranslation } from "react-i18next";
import { Button, Divider, Grid, IconButton, TextField, Typography } from "@mui/material";
import { DownloadFile } from "../../services/FileServices";
import { EndPoints } from "../../axios/endPoints";
import { handleToggleTrainingModelDetailsDialog } from "../../store/mlcreator-slice/mlcreator";
import DynamicInput from "../common/DynamicInput";
import CloudDownloadTwoToneIcon from "@mui/icons-material/CloudDownloadTwoTone";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SinglePredictionResultDialog from "./SinglePredictionResultDialog";
import TrainingModelDetails from "../ml-creator/TrainingModelDetails";

interface PredictionModelProps {
    predictionDataId: string | null,
    predictionDataName: string | null
}

const PredictionModel = ({ predictionDataId, predictionDataName } : PredictionModelProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    
    const [predictionName, setPredictionName] = useState('');
    const { 
        predictionInProgress, 
        predictionTrainingDatasetId, 
        predictionColumnsLoading, 
        predictionDataInputColumns,
        predictionIsShared,
        predictionIsDisabled 
    } = useAppSelector((state: RootState) => state.prediction);
    
    const handlePredict = useCallback(() => {
        if(!predictionName) {
            dispatch(
                enqueueSnackbar({
                    message: t(`PREDICTION_NAME_VALIDATION`),
                    options: { variant: "error", persist: true },
                })
            );
            return;
        }

        if (predictionDataInputColumns.find((el: any) => !el.value)) {
            dispatch(
                enqueueSnackbar({
                    message: t(`PREDICTION_VALIDATION`),
                    options: { variant: "error", persist: true },
                })
            );
            return;
        }

        if (predictionDataId) {
            dispatch(
                PredictSingle({
                    name: predictionName,
                    mlTrainedModelId: parseInt(predictionDataId),
                    inputColumns: predictionDataInputColumns
                })
            ).then((res: any) => {
                if (res && res.payload) {
                    if(res.payload.success) {
                        setPredictionName('');
                    }
                    else {
                        dispatch(
                            enqueueSnackbar({
                                message: t(`${res.payload.message}`),
                                options: {
                                    variant: handleNotificationVariant(res.payload.success),
                                    persist: !res.payload.success,
                                },
                            })
                        );
                    }
                }
            });
        }
    }, [dispatch, predictionDataId, predictionName, predictionDataInputColumns, t])
    
    useEffect(() => {
        if (predictionDataId) {
            dispatch(GetPredictionColumns(parseInt(predictionDataId)));
            setPredictionName(''); //Reset Prediction Name on model change
        }
    }, [predictionDataId]);

    return(
        <React.Fragment>
            { predictionColumnsLoading ? (
                <Grid mt={1.5} container display="flex" justifyContent="center">
                    <Grid item>
                        <Typography variant="body1">{t("PREDICTION_LOADING_COLUMNS")}</Typography>
                    </Grid>
                </Grid>
            ) : (
                <React.Fragment>
                    { 
                        !predictionDataInputColumns?.length ? (
                            <Grid mt={1.5} container display="flex" justifyContent="center">
                                <Grid item>
                                    <Typography variant="body1">{t("PREDICTION_NO_COLUMNS_AVAILABLE")}</Typography>
                                </Grid>
                            </Grid>
                    ) : 
                    (

                    <Grid display="flex" justifyContent="center" flexDirection="column">
                        <Grid
                            container
                            mb={1.5}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            >
                            <Grid item>
                                <Grid container spacing={0.5} display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                                    {!predictionIsShared && (
                                    <Grid item >
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                DownloadFile(
                                                    EndPoints.ML_CREATOR_DOWNLOAD_TRAINING_DATASET +
                                                    `?id=${predictionTrainingDatasetId}`
                                                );
                                            }}
                                        >
                                            <CloudDownloadTwoToneIcon className="download-icon" />
                                        </IconButton>
                                    </Grid>
                                    )}
                                    <Grid item>
                                        <IconButton
                                            onClick={() => {
                                                if (predictionDataId) {
                                                    dispatch(
                                                        handleToggleTrainingModelDetailsDialog({
                                                            isOpen: true,
                                                            body: {
                                                                id: parseInt(predictionDataId),
                                                                name: predictionDataName,
                                                            },
                                                        })
                                                    );
                                                }
                                            }}
                                        >
                                            <InfoOutlinedIcon className="info-icon" />
                                        </IconButton>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="h6">
                                            {t("SINGLE_PREDICTION_MODEL_NAME", { name: predictionDataName })}
                                            {predictionIsDisabled && (
                                                <span style={{ fontWeight: 'bold', marginLeft: '8px' }} className="info-icon">
                                                    [{t('PREDICTION_DISABLED_MODEL')}]
                                                </span>
                                            )}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                                
                        <fieldset disabled={predictionIsDisabled} style={{
                            border: 0,
                            padding: 0,
                            margin: 0,
                            opacity: predictionIsDisabled ? 0.5 : 1,
                            pointerEvents: predictionIsDisabled ? "none" : "auto"
                        }}>
                        <Grid container spacing={2} display="flex" flexDirection="column" pb={1}>
                            <Grid item>
                                <Grid 
                                    container 
                                    alignItems="center" 
                                    justifyContent="space-between"
                                >
                                    <Grid item>
                                        <TextField 
                                            size="small" 
                                            sx={{ minWidth: 300 }}
                                            label={t('PREDICTION_NAME_TEXTFIELD_LABEL')}
                                            value={predictionName}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                setPredictionName(event.target.value);
                                            }}
                                        />
                                    </Grid>

                                    <Grid item>
                                        <Button
                                            size="small"
                                            onClick={() => dispatch(handleClearAllInputColumns())}
                                            className="button-text-color"
                                            style={{
                                                border: '1px solid var(--button-background-color)'
                                            }}
                                        >
                                            {t("SINGLE_PREDICTION_CLEAR_ALL_BUTTON")}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Divider />
                            </Grid>
                        </Grid>
                        
                        <Grid container display="flex" flexDirection="row" spacing={1.5} mb={2}>
                            {predictionDataInputColumns.map(
                                (el: { name: string; type: string }, index: number) => (
                                    <Grid key={el.name} item xs={12} md={6} lg={6}>
                                        <DynamicInput
                                            columns={predictionDataInputColumns}
                                            name={el.name}
                                            type={el.type}
                                            index={index}
                                            onSave={savePredictionValue}
                                        />
                                    </Grid>
                                )
                            )}
                        </Grid>
                        <Button
                            disabled={predictionInProgress}
                            onClick={() => handlePredict()}
                            variant="contained"
                            className="button-color"
                            size="small"
                        >
                            {t("PREDICTION_BUTTON")}
                        </Button>
                        </fieldset>
                    </Grid>
                )}
                </React.Fragment>
            )}
            <SinglePredictionResultDialog />
            <TrainingModelDetails />
        </React.Fragment>
    )
}

export default memo(PredictionModel);